import React, { useMemo, createContext, useContext, useRef, useEffect, useState, useCallback, useReducer, useLayoutEffect } from 'react';
import { dataMapping, fetchData, tokenize, evalExpression, ALL_PROPS, getStateFields } from '@brickd/utils';
import { isEqual, each } from 'lodash';

function useComponentProps(prevProps, pageState, rest) {
    return useMemo(() => {
        const props = dataMapping(prevProps, pageState);
        return rest ? { ...props, ...rest } : props;
    }, [prevProps, pageState, rest]);
}

const StaticContext = createContext({
    pageConfig: {},
    componentsMap: {},
    props: {},
    options: undefined,
    pageName: null,
});
const StaticContextProvider = (props) => (React.createElement(StaticContext.Provider, Object.assign({}, props)));

const getFetchData = async (prevApi, nextApi, prevData, nextData, isFirst, options, fetcher) => {
    if (!nextApi)
        return;
    const result = await fetchData(fetcher, prevApi, nextApi, prevData, nextData, isFirst, options);
    const { state, pageState } = (result || {});
    if (state) {
        nextData.setState(state);
    }
    if (pageState) {
        nextData.setPageState(pageState);
    }
};
function useService(state, api) {
    const { options } = useContext(StaticContext);
    const prevState = useRef(state);
    const prevApi = useRef(api);
    useEffect(() => {
        getFetchData(undefined, api, undefined, state, true, options);
    }, []);
    useEffect(() => {
        if (prevState.current) {
            getFetchData(prevApi.current, api, prevState.current, state, false, options);
        }
    }, [prevApi, api, prevState, state]);
}

function useHiddenComponent(pageState, condition) {
    return useMemo(() => condition && condition.includes('$')
        ? tokenize(condition, pageState)
        : evalExpression(condition, pageState), [pageState, condition]);
}

function useBrickdState(propsState) {
    const [brickdState, setBrickdState] = useState(propsState);
    const prevPropsState = useRef(propsState);
    useEffect(() => {
        if (propsState && !isEqual(propsState, prevPropsState.current)) {
            prevPropsState.current = propsState;
            setBrickdState(propsState);
        }
    }, [propsState, prevPropsState.current, setBrickdState]);
    const setState = useCallback((newState) => setBrickdState({ ...brickdState, ...newState }), [brickdState, setBrickdState]);
    const state = useMemo(() => ({ ...brickdState, setState }), [
        setState,
        brickdState,
    ]);
    return { state, setState };
}

function useForceRender() {
    const [, forceRender] = useReducer((s) => s + 1, 0);
    return forceRender;
}

const BrickStoreContext = createContext(null);
const BrickStoreProvider = (props) => {
    return React.createElement(BrickStoreContext.Provider, Object.assign({}, props));
};

function get(obj, path) {
    return path.split('.').reduce((a, c) => a && a[c], obj);
}
function shallowEqual(objA, objB) {
    if (objA !== objB && (typeof objA !== 'object' || typeof objB !== 'object'))
        return true;
    for (const k of Object.keys(objA)) {
        if (objA[k] !== objB[k])
            return false;
    }
    return true;
}
const handleState = (selector, storeState, stateDeep) => {
    if (selector.includes(ALL_PROPS)) {
        return storeState;
    }
    return selector.reduce((states, key) => {
        let selectedState = storeState[key];
        if (stateDeep) {
            key = stateDeep.split('.').pop();
            selectedState = get(selectedState, stateDeep);
        }
        states[key] = selectedState;
        return states;
    }, {});
};
function useSelectorWithStore(selector, store, controlUpdate, stateDeep, isRoot) {
    const forceRender = useForceRender();
    const prevSelector = useRef([]);
    const prevStoreState = useRef();
    const prevSelectedState = useRef({});
    const storeState = store.getPageState(isRoot);
    let selectedState;
    if (storeState && storeState !== prevStoreState.current) {
        selectedState = handleState(selector, storeState, stateDeep);
    }
    else {
        selectedState = prevSelectedState.current;
    }
    useLayoutEffect(() => {
        prevSelector.current = selector;
        prevStoreState.current = storeState;
        prevSelectedState.current = selectedState;
    });
    useLayoutEffect(() => {
        function checkForUpdates() {
            const storeState = store.getPageState(isRoot);
            const nextSelectedState = storeState
                ? handleState(prevSelector.current, storeState, stateDeep)
                : storeState;
            if (shallowEqual(nextSelectedState, prevSelectedState.current) ||
                (controlUpdate &&
                    !controlUpdate(prevSelectedState.current, nextSelectedState))) {
                return;
            }
            prevStoreState.current = storeState;
            prevSelectedState.current = nextSelectedState;
            forceRender();
        }
        checkForUpdates();
        const unsubscribe = store.subscribe(checkForUpdates);
        return unsubscribe;
    }, [store]);
    return store.isPageStore
        ? {
            ...selectedState,
            setPageState: store.setPageState,
            getPageState: store.getPageState,
        }
        : selectedState;
}
function useBrickSelector(selector, controlUpdate, stateDeep, context = BrickStoreContext, isRoot) {
    const store = useContext(context);
    return useSelectorWithStore(selector, store, controlUpdate, stateDeep, isRoot);
}

const FunParamContext = createContext(null);
const FunParamContextProvider = (props) => (React.createElement(FunParamContext.Provider, Object.assign({}, props)));

const PropsContext = createContext({});
const PropsProvider = (props) => {
    return React.createElement(PropsContext.Provider, Object.assign({}, props));
};

const MapNodeContext = createContext(null);
const MapNodeContextProvider = (props) => (React.createElement(MapNodeContext.Provider, Object.assign({}, props)));

function useGetState(propsState, selector) {
    const { state } = useBrickdState(propsState);
    const brickdState = useBrickSelector(selector);
    const props = useContext(PropsContext);
    const funParams = useContext(FunParamContext);
    const mapItem = useContext(MapNodeContext) || {};
    return useMemo(() => {
        let index = mapItem.index;
        if (funParams) {
            for (const param of funParams) {
                if (typeof param === 'number' || !isNaN(parseInt(param))) {
                    index = param;
                    break;
                }
            }
        }
        return new Proxy({ ...brickdState, ...state, funParams, props, ...mapItem, index }, {
            get: function (target, propKey, receiver) {
                return Reflect.get(target, propKey, receiver);
            },
            set: function (target, propKey, value, receiver) {
                if (propKey in brickdState) {
                    brickdState.setPageState({ [propKey]: value });
                }
                else {
                    state.setState({ [propKey]: value });
                }
                return Reflect.set(target, propKey, value, receiver);
            },
        });
    }, [state, brickdState, funParams, props, mapItem]);
}

function useCommon(vNode, rest, childSelector = []) {
    const { props: prevProps, state, api, condition, isStateDomain } = vNode;
    const selector = useMemo(() => [...getStateFields({ prevProps, api, condition }), ...childSelector], [prevProps, api, condition, childSelector]);
    // eslint-disable-next-line no-undef
    const pageState = useGetState(isStateDomain ? state : {}, isStateDomain ? [ALL_PROPS] : selector);
    useService(pageState, api);
    const props = useComponentProps(prevProps, pageState, rest);
    const hidden = useHiddenComponent(pageState, condition);
    return { props, hidden, pageState };
}

const unListener = (listeners, listener) => {
    const index = listeners.indexOf(listener);
    listeners.splice(index, 1);
};
const executeListener = (listeners) => {
    each(listeners, (listener) => listener());
};
class BrickStore {
    constructor(propState) {
        this.isPageStore = true;
        this.listeners = [];
        this.listenerMap = {};
        this.getPageState = () => this.state;
        this.setPageState = (newState, isReplace, executeKey) => {
            if (isReplace) {
                this.state = newState;
            }
            else {
                this.state = { ...this.state, ...newState };
            }
            executeListener(this.listeners);
            if (executeKey && this.listenerMap[executeKey]) {
                executeListener(this.listenerMap[executeKey]);
            }
        };
        this.subscribe = (listener, key) => {
            let isSubscribed = true;
            if (!key) {
                this.listeners.push(listener);
            }
            else {
                if (this.listenerMap[key]) {
                    (this.listenerMap[key]).push(listener);
                }
                else {
                    this.listenerMap[key] = [listener];
                }
            }
            return () => {
                if (!isSubscribed) {
                    return;
                }
                isSubscribed = false;
                if (!key) {
                    unListener(this.listeners, listener);
                }
                else {
                    unListener(this.listenerMap[key], listener);
                    if (!this.listenerMap[key].length) {
                        delete this.listenerMap[key];
                    }
                }
            };
        };
        this.executeKeyListener = (selectedKey) => {
            if (selectedKey) {
                executeListener(this.listenerMap[selectedKey]);
            }
            else {
                each(this.listenerMap, (listeners) => {
                    executeListener(listeners);
                });
            }
        };
        this.state = propState || {};
    }
}

function useRedux(propsState) {
    const brickStore = useRef(new BrickStore(propsState)).current;
    const prevPropsState = useRef(propsState).current;
    useMemo(() => {
        if (!isEqual(prevPropsState, propsState))
            brickStore.setPageState(propsState, true);
        return;
    }, [propsState, prevPropsState]);
    return brickStore;
}

function useGetProps(state, props) {
    const parentProps = useContext(PropsContext);
    return useMemo(() => {
        return { ...state, ...props, ...parentProps };
    }, [parentProps, state, props]);
}

function useDragMove(getTarget) {
    const originPositionRef = useRef({ isMove: false });
    const onMoveStart = useCallback(function (event) {
        event.stopPropagation();
        const target = getTarget();
        if (!target)
            return;
        const { top, left } = getComputedStyle(target, '');
        originPositionRef.current = {
            x: event.clientX,
            y: event.clientY,
            top: Number.parseInt(top),
            left: Number.parseInt(left),
            isMove: true,
        };
        target.style.pointerEvents = 'none';
        target.style.transition = 'none';
    }, []);
    const onMoveEnd = useCallback(function (event) {
        event.stopPropagation();
        originPositionRef.current.isMove = false;
        getTarget().style.pointerEvents = 'auto';
    }, []);
    const onMove = useCallback(function (event) {
        event.stopPropagation();
        if (!originPositionRef.current.isMove)
            return;
        const target = getTarget();
        const { clientX, clientY } = event;
        const { x, y, top, left } = originPositionRef.current;
        const offsetY = clientY - y;
        const offsetX = clientX - x;
        const newLeft = left + offsetX;
        const newTop = top + offsetY;
        target.style.left = newLeft + 'px';
        target.style.top = newTop + 'px';
        originPositionRef.current = {
            x: clientX,
            y: clientY,
            top: newTop,
            left: newLeft,
            isMove: true,
        };
    }, []);
    return { onMoveStart, onMove, onMoveEnd };
}

function css(el) {
    const style = el && el.style;
    if (style) {
        let cssResult;
        if (getComputedStyle) {
            cssResult = getComputedStyle(el, '');
        }
        else if (el.currentStyle) {
            cssResult = el.currentStyle;
        }
        return cssResult;
    }
}
function formatUnit(target) {
    return isNaN(Number.parseInt(target)) ? null : Number.parseInt(target);
}

var Direction;
(function (Direction) {
    Direction["top"] = "top";
    Direction["right"] = "right";
    Direction["bottom"] = "bottom";
    Direction["left"] = "left";
    Direction["topRight"] = "topRight";
    Direction["bottomRight"] = "bottomRight";
    Direction["bottomLeft"] = "bottomLeft";
    Direction["topLeft"] = "topLeft";
})(Direction || (Direction = {}));
function useResize(targetRef) {
    const originSizeRef = useRef();
    const onResizeEnd = useCallback(() => {
        originSizeRef.current = undefined;
    }, []);
    const onResize = useCallback((event) => {
        event.stopPropagation();
        if (originSizeRef.current) {
            const { clientX, clientY } = event;
            const { x, y, direction, height, width } = originSizeRef.current;
            let offsetY = 0;
            let offsetX = 0;
            switch (direction) {
                case Direction.left:
                    offsetX = x - clientX;
                    break;
                case Direction.right:
                    offsetX = clientX - x;
                    break;
                case Direction.top:
                    offsetY = y - clientY;
                    break;
                case Direction.bottom:
                    offsetY = clientY - y;
                    break;
                case Direction.topLeft:
                    offsetY = y - clientY;
                    offsetX = x - clientX;
                    break;
                case Direction.topRight:
                    offsetY = y - clientY;
                    offsetX = clientX - x;
                    break;
                case Direction.bottomLeft:
                    offsetX = x - clientX;
                    offsetY = clientY - y;
                    break;
                case Direction.bottomRight:
                    offsetY = clientY - y;
                    offsetX = clientX - x;
                    break;
            }
            const heightResult = height + offsetY;
            const widthResult = width + offsetX;
            const { minWidth, maxHeight, maxWidth, minHeight, } = originSizeRef.current;
            targetRef.current.style.transition = 'none';
            if (offsetX !== 0 &&
                (typeof minWidth !== 'number' || widthResult >= minWidth) &&
                (typeof maxWidth !== 'number' || widthResult <= maxWidth)) {
                targetRef.current.style.width = `${widthResult}px`;
            }
            if (offsetY !== 0 &&
                (typeof minHeight !== 'number' || heightResult >= minHeight) &&
                (typeof maxHeight !== 'number' || heightResult <= maxHeight)) {
                targetRef.current.style.height = `${heightResult}px`;
            }
        }
    }, []);
    const onResizeStart = useCallback(function (event, direction) {
        const { width, height, minWidth, minHeight, maxWidth, maxHeight, } = css(targetRef.current);
        originSizeRef.current = {
            x: event.clientX,
            y: event.clientY,
            direction,
            width: formatUnit(width),
            height: formatUnit(height),
            minWidth: formatUnit(minWidth),
            minHeight: formatUnit(minHeight),
            maxWidth: formatUnit(maxWidth),
            maxHeight: formatUnit(maxHeight),
        };
    }, []);
    return { onResizeStart, onResize, onResizeEnd };
}

class BrickObserver {
    constructor() {
        this.subjects = [];
        this.subjectsMap = {};
        this.addSubject = (newSubject) => {
            if (typeof newSubject === 'function') {
                this.subjects.push(newSubject);
            }
            else {
                this.subjectsMap = { ...this.subjectsMap, ...newSubject };
            }
        };
        this.executeSubject = () => {
            this.subjects.forEach((v) => v());
            this.subjects.length = 0;
            each(this.subjectsMap, (v) => v());
            this.subjectsMap = {};
        };
        this.cleanSubject = () => {
            this.subjects.length = 0;
            this.subjectsMap = {};
        };
    }
}

let BRICK_OBSERVER = null;
const setBrickObserver = (brickObserver) => BRICK_OBSERVER = brickObserver;
const BrickObserverContext = createContext(null);
const BrickObserverProvider = (props) => {
    if (!BRICK_OBSERVER) {
        BRICK_OBSERVER = new BrickObserver();
    }
    return React.createElement(BrickObserverContext.Provider, Object.assign({ value: BRICK_OBSERVER }, props));
};

function useBrickObserver() {
    return useContext(BrickObserverContext);
}

export { BrickObserver, BrickObserverContext, BrickObserverProvider, BrickStore, BrickStoreContext, BrickStoreProvider, Direction, FunParamContext, FunParamContextProvider, MapNodeContext, MapNodeContextProvider, PropsContext, PropsProvider, StaticContext, StaticContextProvider, css, formatUnit, get, setBrickObserver, shallowEqual, useBrickObserver, useBrickSelector, useBrickdState, useCommon, useComponentProps, useDragMove, useForceRender, useGetProps, useGetState, useHiddenComponent, useRedux, useResize, useService };
